import { Injectable } from '@angular/core';
import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';
import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem, ReadFileResult, WriteFileResult } from '@capacitor/filesystem';
import imageCompression from "browser-image-compression";
import { FileType } from '../model/file.type';
import { FileService } from './file.service';
import { PermissionsService } from './permissions.service';
import { SentryService } from './sentry.service';
import { normalizeFilePath } from '../utils/file-path.util';


@Injectable({
  providedIn: 'root'
})
export class PhotoService {

  constructor(private readonly fileService: FileService,
    private readonly sentryService: SentryService,
    private readonly permissionsService: PermissionsService
  ) {

  }
  private isNativePlatform(): boolean {
    return Capacitor.getPlatform() !== 'web';
  }

  /** Configuración de compresión unificada para IA (Android + PWA) */
  private readonly COMPRESSION_CONFIG = {
    maxWidthOrHeight: 2048,
    maxSizeMB: 1.5,
    initialQuality: 0.92,
    useWebWorker: true,
    fileType: 'image/jpeg' as const,
  };

  async compressAndStore(type: FileType): Promise<WriteFileResult> {
    try {
      const cameraPermission = await this.permissionsService.requestCameraPermission();
      if (!cameraPermission.granted) {
        console.warn('Permiso de camara no otorgado');
        return null;
      }

      const isNative = this.isNativePlatform();
      const photo: Photo = await Camera.getPhoto({
        resultType: isNative ? CameraResultType.Uri : CameraResultType.Base64,
        allowEditing: false,
        source: CameraSource.Camera,
        quality: 90,
        ...(isNative
          ? { correctOrientation: true, saveToGallery: false }
          : { width: 1920, height: 1080, correctOrientation: true, saveToGallery: false }
        ),
      });

      return await this.processCapturedPhoto(photo, type);
    } catch (e) {
      if (e instanceof Error && e.message === 'User cancelled photos app') {
        return null;
      }
      this.sentryService.captureException(e, { extra: { fileType: type, errorType: 'compress_and_store_failed' } });
      throw e;
    }
  }

  /**
   * Procesa una foto ya capturada (en vivo o restaurada vía appRestoredResult):
   * lee el blob original, comprime, guarda en filesystem y verifica el resultado.
   * Reutilizable para reanudar el procesado tras un reinicio de la app.
   */
  async processCapturedPhoto(photo: Photo, type: FileType): Promise<WriteFileResult> {
    const isNative = this.isNativePlatform();

    // Obtener blob original
    let originalBlob: Blob;
    if (isNative) {
      if (!photo.path) throw new Error('Camera.getPhoto no devolvio un path valido');
      try {
        const read = await Filesystem.readFile({ path: photo.path });
        const r = await fetch('data:image/jpeg;base64,' + read.data);
        originalBlob = await r.blob();
      } catch {
        const r = await fetch(photo.path);
        originalBlob = await r.blob();
      }
      try { await Filesystem.deleteFile({ path: photo.path }); } catch { }
    } else {
      if (!photo.base64String) throw new Error('Camera.getPhoto no devolvio datos base64');
      const r = await fetch('data:image/jpeg;base64,' + photo.base64String);
      originalBlob = await r.blob();
      photo.base64String = null;
      photo.dataUrl = null;
      photo.webPath = null;
    }

    const format = photo.format || 'jpeg';

    // Comprimir con parámetros IA
    let result: WriteFileResult;
    try {
      const compressed = await imageCompression(
        new File([originalBlob], 'photo.jpeg', { type: 'image/jpeg' }),
        this.COMPRESSION_CONFIG
      );
      result = await this.fileService.storeBase64File(await this.blobToDataUrl(compressed), format, type);
    } catch {
      // Si falla la compresión, guardar el original sin comprimir
      result = await this.fileService.storeBase64File(await this.blobToDataUrl(originalBlob), format, type);
    }

    // Verificar que el archivo se guardo correctamente
    if (result) {
      await this.verifyStoredFile(result.uri, type);
    }

    return result;
  }

  private fileToBase64(file: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('No se pudo leer el archivo comprimido'));
      reader.onload = () => {
        const dataUrl = String(reader.result);
        const i = dataUrl.indexOf(',');
        resolve(i >= 0 ? dataUrl.slice(i + 1) : dataUrl);
      };
      reader.readAsDataURL(file);
    });
  }

  /**
   * Convierte un Blob a data URL completa (conserva el prefijo "data:image/...").
   * A diferencia de fileToBase64() que strippea el prefijo, este lo conserva
   * para que el formato de almacenamiento sea autocontenido.
   */
  private blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () =>
        reject(new Error('No se pudo leer el archivo para data URL'));
      reader.onload = () => resolve(String(reader.result));
      reader.readAsDataURL(blob);
    });
  }

  async removeFromPath(path: string): Promise<void> {
    try {
      // Verificar si el archivo existe antes de intentar eliminarlo
      const exists = await this.fileService.fileExists(path);
      if (exists) {
        await this.fileService.removeFileFromPath(path);
      } else {
        console.warn('Archivo no existe, no se puede eliminar:', path);
      }
    } catch (error) {
      console.warn('Error al eliminar archivo:', path, error);
      // No lanzar el error, solo registrarlo
    }
  }

  async getFromPath(path: string): Promise<ReadFileResult> {
    return await this.fileService.getFileFromPath(path);
  }

  /**
   * Verifica que un archivo recien guardado tenga un tamano razonable.
   * Si el archivo esta vacio, registra en Sentry.
   */
  private async verifyStoredFile(
    path: string,
    type: FileType
  ): Promise<void> {
    try {
      const stat = await this.fileService.getFileStat(path);
      if (!stat || stat.size === 0) {
        this.sentryService.captureMessage(
          'Archivo guardado con tamano 0: ' + path,
          {
            level: 'warning',
            extra: {
              path,
              fileType: type,
              errorType: 'stored_file_zero_size',
            },
          }
        );
      }
    } catch (err) {
      this.sentryService.captureException(err, {
        extra: {
          path,
          fileType: type,
          errorType: 'verify_stored_file_failed',
        },
      });
    }
  }

  /**
   * Devuelve una URL lista para usar en <img src>.
   * - Nativo: getUri + convertFileSrc => URL directa al archivo (~100 bytes).
   * - Web: data URL desde Filesystem (~tamano del archivo).
   *
   * Reemplaza a getBase64PhotoByPath() para previews.
   */
  async getFileUrl(path: string): Promise<string> {
    if (!path) {
      throw new Error('Path de imagen invalido');
    }

    if (this.isNativePlatform()) {
      const normalizedPath = normalizeFilePath(path);
      const result = await Filesystem.readFile({ path: normalizedPath, directory: Directory.Data });
      const rawData = result.data as string;
      return rawData.startsWith('data:')
        ? rawData
        : 'data:image/jpeg;base64,' + rawData;
    }

    // Web fallback: leer y devolver data URL
    const exists = await this.fileService.fileExists(path);
    if (!exists) {
      const fileName = path.split('/').pop();
      throw new Error('Archivo no encontrado: ' + fileName);
    }

    const result = await Filesystem.readFile({ path });
    const rawData = result.data as string;
    return rawData.startsWith('data:')
      ? rawData
      : 'data:image/jpeg;base64,' + rawData;
  }

  /**
   * Lee un archivo y devuelve un Blob listo para upload.
   *
   * - Nativo: getUri + convertFileSrc + fetch => Blob directo del disco, cero base64.
   * - Web: readFile + fetch(dataUrl) => Blob, decodificacion nativa del navegador.
   *
   * Validaciones (ambas plataformas):
   *   1. Archivo existe
   *   2. Blob size >= 128 bytes
   *   3. Magic bytes JPEG (0xFF 0xD8)
   */
  async getBlobFromPath(path: string): Promise<Blob> {
    try {
      // 0. Normalizar ruta (en nativo WriteFileResult.uri es file:///data/.../files/{type}/{name})
      // 1. Verificar existencia con path original
      const exists = await this.fileService.fileExists(path);
      if (!exists) {
        throw new Error('Archivo no encontrado: ' + path);
      }

      // 2. Normalizar ruta para getUri
      const normalizedPath = normalizeFilePath(path);

      let blob: Blob;

      // 2. Ruta nativa: acceso binario directo
      if (this.isNativePlatform()) {
        const uri = await Filesystem.getUri({
          path: normalizedPath,
          directory: Directory.Data,
        });
        const fileUrl = Capacitor.convertFileSrc(uri.uri);
        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error(
            'Error al leer archivo (fetch status ' +
              response.status +
              '): ' +
              path
          );
        }
        blob = await response.blob();
      } else {
        // 3. Ruta web: leer como string y decodificar con fetch
        const result = await Filesystem.readFile({ path });
        const rawData = result.data as string;
        if (!rawData || rawData.length === 0) {
          throw new Error('Archivo vacio: ' + path);
        }
        const dataUrl = rawData.startsWith('data:')
          ? rawData
          : 'data:image/jpeg;base64,' + rawData;
        const response = await fetch(dataUrl);
        if (!response.ok) {
          throw new Error('Error al decodificar imagen: ' + path);
        }
        blob = await response.blob();
      }

      // 4. Validar tamano minimo
      if (blob.size < 128) {
        throw new Error(
          'Imagen corrupta o demasiado pequena (' +
            blob.size +
            ' bytes): ' +
            path
        );
      }

      // 5. Validar magic bytes JPEG
      const headerBytes = new Uint8Array(
        await blob.slice(0, 2).arrayBuffer()
      );
      if (headerBytes[0] !== 0xff || headerBytes[1] !== 0xd8) {
        throw new Error(
          'El archivo no contiene una imagen JPEG valida: ' + path
        );
      }

      return blob;
    } catch (error) {
      const message = error?.message || String(error);
      if (message.includes('Archivo no encontrado')) {
        // El archivo referenciado ya no existe. Se propaga el error controlado
        // para que el flujo de envío lo traduzca a un mensaje accionable.
        this.sentryService.captureMessage(
          'Archivo no encontrado al leer blob (se propaga error controlado): ' + path,
          {
            level: 'warning',
            extra: { path, errorType: 'get_blob_from_path_missing' },
          }
        );
      } else {
        this.sentryService.captureException(error, {
          extra: {
            path,
            errorType: 'get_blob_from_path_failed',
            errorMessage: message,
          },
        });
      }
      throw error;
    }
  }

  async getBase64PhotoByPath(path: string): Promise<string> {
    try {
      // Validar que path no sea null o undefined
      if (!path) {
        console.warn('Path de imagen es null o undefined');
        throw new Error('Path de imagen inválido');
      }

      // Verificar si el archivo existe antes de intentar leerlo
      const exists = await this.fileService.fileExists(path);
      if (!exists) {
        const fileName = path.split('/').pop();
        console.warn(`Archivo no encontrado (puede haber sido eliminado): ${fileName}`);

        // No enviar a Sentry si el archivo simplemente no existe
        // Esto es esperado cuando se limpia después de envío exitoso
        throw new Error(`Archivo no encontrado: ${fileName}`);
      }

      const result = await Filesystem.readFile({
        path,
      });
      return `data:image/jpeg;base64,${result.data}`;
    } catch (error) {
      // Solo capturar en Sentry si NO es un error de archivo no encontrado
      const errorMessage = error?.message || String(error);
      const fileNotExists = errorMessage.includes('Archivo no encontrado') ||
        errorMessage.includes('does not exist') ||
        errorMessage.includes('not found') ||
        errorMessage.includes('No such file');

      if (!fileNotExists) {
        // Solo reportar errores inesperados de lectura
        this.sentryService.captureException(error, {
          extra: {
            path,
            errorType: 'file_read_error',
            errorMessage
          }
        });
      }

      throw error;
    }
  }

}
